// Draft Manager - Handles draft logic and state management

/**
 * Draft Order Types:
 * - 'snake': Snake draft (1-2-3-4-4-3-2-1-1-2-3-4...)
 * - 'linear': Linear draft (1-2-3-4-1-2-3-4-1-2-3-4...)
 */

// Initialize a new draft
export const initializeDraft = (league, draftSettings = {}) => {
  // Validate league data
  if (!league) {
    throw new Error('League data is required to initialize draft');
  }
  
  if (!league.teams || !Array.isArray(league.teams)) {
    throw new Error('League must have teams to initialize draft');
  }
  
  // Filter out invited teams (only include active/joined teams)
  const teams = league.teams.filter(t => t.status !== 'invited' && t.status !== 'declined');
  
  if (teams.length === 0) {
    throw new Error('No active teams in league. At least one team is required to start a draft.');
  }
  
  console.log(`📋 Initializing draft with ${teams.length} teams`);
  
  // Default draft settings
  const settings = {
    type: draftSettings.draftType || draftSettings.type || 'snake',
    rounds: draftSettings.rounds || 25,
    pickTimeLimit: draftSettings.pickTimeLimit || 90, // seconds
    autoPickEnabled: draftSettings.autoPickEnabled !== false,
    autoDraftTeamIds: draftSettings.autoDraftTeamIds || [], // teams that timed out; always auto-pick
    draftOrderType: draftSettings.draftOrderType || 'random',
    customDraftOrder: draftSettings.customDraftOrder || teams.map(t => t.id),
    ...draftSettings
  };

  // Generate draft order based on settings
  let firstRoundOrder;
  
  if (settings.draftOrderType === 'manual' && settings.customDraftOrder) {
    // Use commissioner's custom order
    firstRoundOrder = settings.customDraftOrder.filter(teamId => 
      teams.some(t => t.id === teamId)
    );
  } else {
    // Random order - shuffle team IDs
    firstRoundOrder = [...teams.map(t => t.id)].sort(() => Math.random() - 0.5);
  }
  
  // Generate full draft order for all rounds
  const draftOrder = generateDraftOrder(firstRoundOrder, settings.type, settings.rounds);

  const draftTeams = teams.map(team => ({
    id: team.id,
    name: team.name,
    owner: team.owner,
    logoEmoji: team.logoEmoji || '⚾',
    logoColor: team.logoColor || '#007bff',
    picks: []
  }));

  const draft = {
    leagueId: league.leagueId || league.id,
    status: 'not_started', // 'not_started', 'in_progress', 'paused', 'completed'
    settings,
    teams: draftTeams,
    picks: [], // Array of all picks made
    currentPickNumber: 0,
    currentPickTeamId: draftOrder[0],
    draftOrder,
    startedAt: null,
    completedAt: null,
    pausedAt: null,
    pickStartTime: null
  };

  console.log('📋 Draft initialized:', {
    leagueId: draft.leagueId,
    teams: draft.teams.length,
    rounds: settings.rounds,
    draftOrder: draft.draftOrder.slice(0, 10) // First 10 picks
  });

  return draft;
};

// Generate draft order based on type
// teamIds can be either an array of team objects or an array of team IDs
const generateDraftOrder = (teamIds, type, rounds) => {
  const order = [];
  
  // If teamIds contains objects, extract just the IDs
  const ids = Array.isArray(teamIds) 
    ? teamIds.map(t => typeof t === 'string' ? t : t.id)
    : teamIds;

  for (let round = 0; round < rounds; round++) {
    if (type === 'snake' && round % 2 === 1) {
      // Reverse order for odd rounds (0-indexed)
      order.push(...[...ids].reverse());
    } else {
      // Normal order
      order.push(...ids);
    }
  }

  return order;
};

// Get current pick information
export const getCurrentPickInfo = (draft) => {
  if (!draft || draft.status === 'completed') {
    return null;
  }

  const pickNumber = draft.currentPickNumber;
  const teamId = draft.draftOrder[pickNumber];
  const team = draft.teams.find(t => t.id === teamId);
  const round = Math.floor(pickNumber / draft.teams.length) + 1;
  const pickInRound = (pickNumber % draft.teams.length) + 1;
  const totalPicks = draft.draftOrder.length;

  return {
    pickNumber: pickNumber + 1,
    round,
    pickInRound,
    teamId,
    team,
    totalPicks,
    isLastPick: pickNumber === totalPicks - 1
  };
};

// Make a draft pick
export const makePick = (draft, playerId, playerData) => {
  const currentPick = getCurrentPickInfo(draft);
  
  if (!currentPick) {
    return { success: false, error: 'Draft is not active' };
  }

  // Check if player is already drafted
  const alreadyDrafted = draft.picks.some(p => p.playerId === playerId);
  if (alreadyDrafted) {
    return { success: false, error: 'Player already drafted' };
  }

  const pick = {
    pickNumber: currentPick.pickNumber,
    round: currentPick.round,
    pickInRound: currentPick.pickInRound,
    teamId: currentPick.teamId,
    teamName: currentPick.team.name,
    playerId,
    playerName: playerData.name,
    playerPosition: playerData.position,
    playerTeam: playerData.team,
    timestamp: new Date().toISOString()
  };

  const updatedDraft = {
    ...draft,
    picks: [...draft.picks, pick],
    currentPickNumber: draft.currentPickNumber + 1,
    currentPickTeamId: draft.draftOrder[draft.currentPickNumber + 1],
    pickStartTime: new Date().toISOString()
  };

  // Update team's picks
  const teamIndex = updatedDraft.teams.findIndex(t => t.id === currentPick.teamId);
  if (teamIndex !== -1) {
    updatedDraft.teams[teamIndex].picks.push(pick);
  }

  // Check if draft is complete
  if (currentPick.isLastPick) {
    updatedDraft.status = 'completed';
    updatedDraft.completedAt = new Date().toISOString();
  }

  return { success: true, draft: updatedDraft, pick };
};

// Start the draft
export const startDraft = (draft) => {
  return {
    ...draft,
    status: 'in_progress',
    startedAt: new Date().toISOString(),
    pickStartTime: new Date().toISOString()
  };
};

// Pause the draft
export const pauseDraft = (draft) => {
  return {
    ...draft,
    status: 'paused',
    pausedAt: new Date().toISOString()
  };
};

// Resume the draft
export const resumeDraft = (draft) => {
  return {
    ...draft,
    status: 'in_progress',
    pausedAt: null,
    pickStartTime: new Date().toISOString()
  };
};

// Undo last pick
export const undoLastPick = (draft) => {
  if (draft.picks.length === 0) {
    return { success: false, error: 'No picks to undo' };
  }

  const lastPick = draft.picks[draft.picks.length - 1];
  const updatedPicks = draft.picks.slice(0, -1);

  // Update team's picks
  const teams = draft.teams.map(team => {
    if (team.id === lastPick.teamId) {
      return {
        ...team,
        picks: team.picks.filter(p => p.pickNumber !== lastPick.pickNumber)
      };
    }
    return team;
  });

  const updatedDraft = {
    ...draft,
    picks: updatedPicks,
    currentPickNumber: draft.currentPickNumber - 1,
    currentPickTeamId: draft.draftOrder[draft.currentPickNumber - 1],
    teams,
    pickStartTime: new Date().toISOString()
  };

  return { success: true, draft: updatedDraft };
};

// Get available players (not yet drafted)
export const getAvailablePlayers = (draft, allPlayers) => {
  const draftedPlayerIds = new Set(draft.picks.map(p => p.playerId));
  return allPlayers.filter(player => !draftedPlayerIds.has(player.id));
};

// Get drafted players for a specific team (teamId can be id or teamId; comparison is string-safe)
export const getTeamDraftPicks = (draft, teamId) => {
  if (!draft?.picks?.length || teamId == null) return [];
  const sid = String(teamId);
  return draft.picks.filter(p => String(p.teamId) === sid);
};

// Get recent picks (last N picks)
export const getRecentPicks = (draft, count = 10) => {
  return draft.picks.slice(-count).reverse();
};

// Get next pick info
export const getNextPickInfo = (draft, lookahead = 1) => {
  const nextPickIndex = draft.currentPickNumber + lookahead;
  
  if (nextPickIndex >= draft.draftOrder.length) {
    return null;
  }

  const teamId = draft.draftOrder[nextPickIndex];
  const team = draft.teams.find(t => t.id === teamId);
  const round = Math.floor(nextPickIndex / draft.teams.length) + 1;
  const pickInRound = (nextPickIndex % draft.teams.length) + 1;

  return {
    pickNumber: nextPickIndex + 1,
    round,
    pickInRound,
    teamId,
    team
  };
};

// Check if it's a specific team's turn
export const isTeamsTurn = (draft, teamId) => {
  return draft.currentPickTeamId === teamId && draft.status === 'in_progress';
};

// Calculate time remaining for current pick
export const getTimeRemaining = (draft) => {
  if (!draft.pickStartTime || draft.status !== 'in_progress') {
    return draft.settings.pickTimeLimit;
  }

  const elapsed = (Date.now() - new Date(draft.pickStartTime).getTime()) / 1000;
  const remaining = Math.max(0, draft.settings.pickTimeLimit - elapsed);
  
  return Math.ceil(remaining);
};

// Auto-pick best available player
export const autoPick = (draft, availablePlayers) => {
  if (availablePlayers.length === 0) {
    return { success: false, error: 'No players available' };
  }

  const currentPick = getCurrentPickInfo(draft);
  const team = currentPick.team;

  // Get team's position needs
  const positionCounts = {};
  team.picks.forEach(pick => {
    const pos = pick.playerPosition;
    positionCounts[pos] = (positionCounts[pos] || 0) + 1;
  });

  // Prioritize positions based on needs
  const positionPriority = ['C', '1B', '2B', '3B', 'SS', 'OF', 'DH', 'SP', 'RP'];
  
  // Find best available player
  let bestPlayer = null;
  
  for (const position of positionPriority) {
    const needsPosition = !positionCounts[position] || positionCounts[position] < 2;
    
    if (needsPosition) {
      bestPlayer = availablePlayers.find(p => p.position === position);
      if (bestPlayer) break;
    }
  }

  // If no specific need, just pick highest ranked available
  if (!bestPlayer) {
    bestPlayer = availablePlayers[0];
  }

  return makePick(draft, bestPlayer.id, bestPlayer);
};

// Save draft to localStorage
export const saveDraft = (draft) => {
  const key = `draft_${draft.leagueId}`;
  localStorage.setItem(key, JSON.stringify(draft));
};

// Load draft from localStorage
export const loadDraft = (leagueId) => {
  const key = `draft_${leagueId}`;
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : null;
};

// Clear draft from localStorage
export const clearDraft = (leagueId) => {
  const key = `draft_${leagueId}`;
  localStorage.removeItem(key);
};

// Get draft statistics
export const getDraftStats = (draft) => {
  const positionCounts = {};
  const teamCounts = {};

  draft.picks.forEach(pick => {
    positionCounts[pick.playerPosition] = (positionCounts[pick.playerPosition] || 0) + 1;
    teamCounts[pick.playerTeam] = (teamCounts[pick.playerTeam] || 0) + 1;
  });

  return {
    totalPicks: draft.picks.length,
    remainingPicks: draft.draftOrder.length - draft.picks.length,
    positionCounts,
    teamCounts,
    averagePickTime: calculateAveragePickTime(draft),
    currentRound: Math.floor(draft.currentPickNumber / draft.teams.length) + 1,
    totalRounds: draft.settings.rounds
  };
};

// Calculate average time per pick
const calculateAveragePickTime = (draft) => {
  if (draft.picks.length < 2) return 0;

  let totalTime = 0;
  for (let i = 1; i < draft.picks.length; i++) {
    const prevTime = new Date(draft.picks[i - 1].timestamp).getTime();
    const currTime = new Date(draft.picks[i].timestamp).getTime();
    totalTime += (currTime - prevTime) / 1000;
  }

  return Math.round(totalTime / (draft.picks.length - 1));
};

export default {
  initializeDraft,
  startDraft,
  pauseDraft,
  resumeDraft,
  makePick,
  undoLastPick,
  getCurrentPickInfo,
  getNextPickInfo,
  getAvailablePlayers,
  getTeamDraftPicks,
  getRecentPicks,
  isTeamsTurn,
  getTimeRemaining,
  autoPick,
  saveDraft,
  loadDraft,
  clearDraft,
  getDraftStats
};

